/**
 * API модуль для работы с оплатой
 * 
 * Предоставляет функции для поиска счетов, генерации платежей
 * и получения информации о счетах авторизованных пользователей.
 */

import { apiClient } from './client';

/**
 * Интерфейс результата поиска счета
 */
export interface AccountSearchResult {
  /** Уникальный идентификатор счета */
  id: string;
  /** Номер лицевого счета */
  accountNumber: string;
  /** Отображаемое имя (ФИО владельца) */
  displayName?: string;
  /** Полный адрес подключения (только для авторизованных) */
  address?: string;
  /** Маскированный адрес (для неавторизованных: улица/дом скрыты, квартира видна) */
  maskedAddress?: string;
  /** Текущий баланс счета */
  balance?: number;
  /** Флаг блокировки счета */
  isBlocked?: boolean;
  /** Дата, до которой оплачены услуги */
  paidUntil?: string | null;
}

/**
 * Интерфейс ответа при поиске счета
 */
export interface AccountSearchResponse {
  /** Массив найденных счетов */
  accounts: AccountSearchResult[];
}

/**
 * Интерфейс данных для генерации платежа
 */
export interface PaymentGenerateRequest {
  /** ID счета для оплаты */
  accountId: string;
  /** Сумма платежа */
  amount: number;
  /** Email плательщика (опционально) */
  email?: string;
  /**
   * Признак годовой оплаты по акции.
   * Сервер дополнительно проверяет, что сумма ≥ абонплата × 12.
   */
  annual?: boolean;
}

/**
 * Интерфейс ответа при генерации платежа
 */
export interface PaymentGenerateResponse {
  /** URL для перенаправления на страницу оплаты */
  paymentUrl: string;
  /** ID созданного счета на оплату */
  invoiceId: string;
}

/**
 * Интерфейс информации о лицевом счете авторизованного пользователя
 */
export interface SubscriberInfo {
  /** Уникальный идентификатор счета */
  id: string;
  /** Номер лицевого счета */
  accountNumber: string;
  /** Адрес подключения */
  address: string;
  /** Текущий баланс счета */
  balance: number;
  /** Флаг блокировки счета */
  isBlocked: boolean;
  /** Дата, до которой оплачены услуги */
  paidUntil?: string | null;
  /** Дата последнего списания */
  lastBillingDate?: string | null;
}

/**
 * Поиск счета по номеру телефона или лицевому счету
 * 
 * Отправляет запрос на сервер для поиска счетов по идентификатору
 * (номер телефона или номер лицевого счета).
 * 
 * @param identifier - Номер телефона или лицевой счет для поиска
 * @returns Promise с массивом найденных счетов
 * @throws Error если произошла ошибка при поиске
 * 
 * @example
 * ```typescript
 * try {
 *   const result = await searchAccount('+79001234567');
 *   console.log('Найдено счетов:', result.accounts.length);
 * } catch (error) {
 *   console.error('Ошибка поиска:', error);
 * }
 * ```
 */
export async function searchAccount(identifier: string): Promise<AccountSearchResponse> {
  const response = await apiClient.post<AccountSearchResponse>(
    '/public/account/search',
    { identifier }
  );
  
  return response.data;
}

/**
 * Генерация платежа для гостя (неавторизованного пользователя)
 * 
 * Создает платеж через платежную систему Robokassa для указанного счета.
 * Возвращает URL для перенаправления пользователя на страницу оплаты.
 * 
 * @param data - Данные для генерации платежа (ID счета и сумма)
 * @returns Promise с URL оплаты и ID счета
 * @throws Error если произошла ошибка при генерации платежа
 * 
 * @example
 * ```typescript
 * try {
 *   const result = await generatePaymentGuest({
 *     accountId: '123',
 *     amount: 500
 *   });
 *   window.location.href = result.paymentUrl;
 * } catch (error) {
 *   console.error('Ошибка генерации платежа:', error);
 * }
 * ```
 */
export async function generatePaymentGuest(
  data: PaymentGenerateRequest
): Promise<PaymentGenerateResponse> {
  const response = await apiClient.post<PaymentGenerateResponse>(
    '/public/payment/robokassa/generate',
    data
  );
  
  return response.data;
}

/**
 * Генерация платежа для авторизованного пользователя
 * 
 * Создает платеж через платежную систему Robokassa для указанного счета.
 * Использует токен авторизации из заголовков запроса.
 * Возвращает URL для перенаправления пользователя на страницу оплаты.
 * 
 * @param data - Данные для генерации платежа (ID счета и сумма)
 * @returns Promise с URL оплаты и ID счета
 * @throws Error если произошла ошибка при генерации платежа
 * 
 * @example
 * ```typescript
 * try {
 *   const result = await generatePaymentAuth({
 *     accountId: '123',
 *     amount: 500
 *   });
 *   window.location.href = result.paymentUrl;
 * } catch (error) {
 *   console.error('Ошибка генерации платежа:', error);
 * }
 * ```
 */
export async function generatePaymentAuth(
  data: PaymentGenerateRequest
): Promise<PaymentGenerateResponse> {
  // Для авторизованных пользователей используем тот же endpoint
  // Токен будет добавлен автоматически через interceptor
  const response = await apiClient.post<PaymentGenerateResponse>(
    '/public/payment/robokassa/generate',
    data
  );
  
  return response.data;
}

/**
 * Получение списка лицевых счетов авторизованного пользователя
 * 
 * Отправляет запрос на сервер для получения всех лицевых счетов,
 * привязанных к текущему авторизованному пользователю.
 * Требует наличия токена авторизации.
 * 
 * @returns Promise с массивом лицевых счетов пользователя
 * @throws Error если пользователь не авторизован или произошла ошибка
 * 
 * @example
 * ```typescript
 * try {
 *   const accounts = await getAuthenticatedAccounts();
 *   console.log('Счетов пользователя:', accounts.length);
 *   accounts.forEach(acc => {
 *     console.log(`${acc.accountNumber}: ${acc.balance}₽`);
 *   });
 * } catch (error) {
 *   console.error('Ошибка получения счетов:', error);
 * }
 * ```
 */
export async function getAuthenticatedAccounts(): Promise<SubscriberInfo[]> {
  const response = await apiClient.get<{ status: string; data: SubscriberInfo[] }>(
    '/customer/subscribers'
  );
  
  return response.data.data;
}
